import React from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

const useStyles = makeStyles(theme => ({
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  formControl: {
    marginTop: theme.spacing(1),
    width: "100%"
  }
}));

const optionalPositiveInteger = label =>
  Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? null : value
    )
    .nullable()
    .min(1, `${label} deve ser maior que zero`);

const optionalString = schema =>
  schema.transform(value => (value === "" ? null : value)).nullable();

const optionalDate = schema =>
  schema.transform(value => (value === "" ? null : value)).nullable();

const sipDomainSchema = optionalString(
  Yup.string().matches(
    /^(?=.{4,253}$)(?!-)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/,
    "Dominio SIP invalido"
  )
);

const createSchema = Yup.object().shape({
  name: Yup.string().required().min(2),
  plan: Yup.string().required().min(2),
  adminName: Yup.string().required().min(2),
  adminEmail: Yup.string().email().required(),
  adminPassword: Yup.string().required().min(8),
  maxUsers: optionalPositiveInteger("Limite de usuarios"),
  maxQueues: optionalPositiveInteger("Limite de filas"),
  maxWhatsapps: optionalPositiveInteger("Limite de conexoes"),
  maxSipExtensions: optionalPositiveInteger("Limite de ramais"),
  monthlyPrice: Yup.number().transform((value, originalValue) =>
    originalValue === "" || originalValue === null ? null : value
  )
    .nullable()
    .min(0),
  currency: Yup.string().required().min(3).max(10),
  subscriptionStatus: Yup.string()
    .required()
    .oneOf(["trial", "active", "past_due", "suspended", "canceled"]),
  trialEndsAt: optionalDate(Yup.date()),
  billingStartsAt: optionalDate(Yup.date()),
  subscriptionEndsAt: optionalDate(Yup.date()),
  lastPaymentAt: optionalDate(Yup.date()),
  sipEnabled: Yup.boolean(),
  sipDomain: sipDomainSchema.when("sipEnabled", {
    is: true,
    then: schema => schema.required("Dominio SIP obrigatorio com SIP habilitado")
  }),
  sipTransport: Yup.string().oneOf(["udp", "tcp", "tls"]),
  sipMediaAddress: optionalString(Yup.string())
});

const updateSchema = Yup.object().shape({
  name: Yup.string().required().min(2),
  status: Yup.string().required().oneOf(["active", "inactive"]),
  plan: Yup.string().required().min(2),
  maxUsers: optionalPositiveInteger("Limite de usuarios"),
  maxQueues: optionalPositiveInteger("Limite de filas"),
  maxWhatsapps: optionalPositiveInteger("Limite de conexoes"),
  maxSipExtensions: optionalPositiveInteger("Limite de ramais"),
  monthlyPrice: Yup.number().transform((value, originalValue) =>
    originalValue === "" || originalValue === null ? null : value
  )
    .nullable()
    .min(0),
  currency: Yup.string().required().min(3).max(10),
  primaryAdminName: optionalString(Yup.string().min(2)),
  primaryAdminEmail: optionalString(Yup.string().email()),
  primaryAdminPassword: optionalString(Yup.string().min(8)),
  subscriptionStatus: Yup.string()
    .required()
    .oneOf(["trial", "active", "past_due", "suspended", "canceled"]),
  trialEndsAt: optionalDate(Yup.date()),
  billingStartsAt: optionalDate(Yup.date()),
  subscriptionEndsAt: optionalDate(Yup.date()),
  lastPaymentAt: optionalDate(Yup.date()),
  sipEnabled: Yup.boolean(),
  sipDomain: sipDomainSchema.when("sipEnabled", {
    is: true,
    then: schema => schema.required("Dominio SIP obrigatorio com SIP habilitado")
  }),
  sipTransport: Yup.string().oneOf(["udp", "tcp", "tls"]),
  sipMediaAddress: optionalString(Yup.string()),
  graceEndsAt: optionalDate(Yup.date()),
  scheduledSuspensionAt: optionalDate(Yup.date()),
  statusReason: optionalString(Yup.string()),
  internalNotes: optionalString(Yup.string()),
  suspendedAt: optionalDate(Yup.date())
});

const toDateInputValue = value => {
  if (!value) {
    return "";
  }

  return new Date(value).toISOString().slice(0, 10);
};

const CompanyModal = ({ open, onClose, onSubmit, company }) => {
  const classes = useStyles();
  const isEditMode = Boolean(company?.id);

  const initialValues = isEditMode
    ? {
        name: company.name || "",
        status: company.status || "active",
        plan: company.plan || "custom",
        maxUsers: company.maxUsers ?? "",
        maxQueues: company.maxQueues ?? "",
        maxWhatsapps: company.maxWhatsapps ?? "",
        maxSipExtensions: company.maxSipExtensions ?? "",
        monthlyPrice: company.monthlyPrice ?? "",
        currency: company.currency || "BRL",
        subscriptionStatus: company.subscriptionStatus || "active",
        trialEndsAt: toDateInputValue(company.trialEndsAt),
        billingStartsAt: toDateInputValue(company.billingStartsAt),
        subscriptionEndsAt: toDateInputValue(company.subscriptionEndsAt),
        lastPaymentAt: toDateInputValue(company.lastPaymentAt),
        sipEnabled: Boolean(company.sipEnabled),
        sipDomain: company.sipDomain || "",
        sipTransport: company.sipTransport || "udp",
        sipMediaAddress: company.sipMediaAddress || "",
        graceEndsAt: toDateInputValue(company.graceEndsAt),
        scheduledSuspensionAt: toDateInputValue(company.scheduledSuspensionAt),
        suspendedAt: toDateInputValue(company.suspendedAt),
        statusReason: company.statusReason || "",
        internalNotes: company.internalNotes || "",
        primaryAdminName: company.primaryAdmin?.name || "",
        primaryAdminEmail: company.primaryAdmin?.email || "",
        primaryAdminPassword: ""
      }
    : {
        name: "",
        plan: "custom",
        adminName: "",
        adminEmail: "",
        adminPassword: "",
        maxUsers: "",
        maxQueues: "",
        maxWhatsapps: "",
        maxSipExtensions: "",
        monthlyPrice: "",
        currency: "BRL",
        subscriptionStatus: "trial",
        sipEnabled: false,
        sipDomain: "",
        sipTransport: "udp",
        sipMediaAddress: "",
        trialEndsAt: toDateInputValue(
          new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        ),
        billingStartsAt: "",
        subscriptionEndsAt: "",
        lastPaymentAt: ""
      };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>{isEditMode ? "Editar empresa" : "Nova empresa"}</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={isEditMode ? updateSchema : createSchema}
        onSubmit={async (values, actions) => {
          await onSubmit(values);
          actions.setSubmitting(false);
        }}
      >
        {({ touched, errors, isSubmitting, values }) => {
          const sipEnabledSelected =
            values.sipEnabled === true || values.sipEnabled === "true";

          return (
          <Form>
            <DialogContent dividers>
              <Field
                as={TextField}
                label="Empresa"
                name="name"
                variant="outlined"
                margin="dense"
                fullWidth
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />

              <FormControl
                variant="outlined"
                margin="dense"
                className={classes.formControl}
              >
                <InputLabel>Plano</InputLabel>
                <Field as={Select} label="Plano" name="plan">
                  <MenuItem value="starter">Starter</MenuItem>
                  <MenuItem value="growth">Growth</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Field>
              </FormControl>

              <Field
                as={TextField}
                label="Limite de usuarios"
                name="maxUsers"
                type="number"
                variant="outlined"
                margin="dense"
                fullWidth
                error={touched.maxUsers && Boolean(errors.maxUsers)}
                helperText={touched.maxUsers && errors.maxUsers}
              />
              <Field
                as={TextField}
                label="Limite de filas"
                name="maxQueues"
                type="number"
                variant="outlined"
                margin="dense"
                fullWidth
                error={touched.maxQueues && Boolean(errors.maxQueues)}
                helperText={touched.maxQueues && errors.maxQueues}
              />
              <Field
                as={TextField}
                label="Limite de conexoes"
                name="maxWhatsapps"
                type="number"
                variant="outlined"
                margin="dense"
                fullWidth
                error={touched.maxWhatsapps && Boolean(errors.maxWhatsapps)}
                helperText={touched.maxWhatsapps && errors.maxWhatsapps}
              />
              <Field
                as={TextField}
                label="Limite de ramais"
                name="maxSipExtensions"
                type="number"
                variant="outlined"
                margin="dense"
                fullWidth
                error={
                  touched.maxSipExtensions && Boolean(errors.maxSipExtensions)
                }
                helperText={touched.maxSipExtensions && errors.maxSipExtensions}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Field
                    as={TextField}
                    label="Mensalidade"
                    name="monthlyPrice"
                    type="number"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                    error={touched.monthlyPrice && Boolean(errors.monthlyPrice)}
                    helperText={touched.monthlyPrice && errors.monthlyPrice}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.formControl}
                  >
                    <InputLabel>Moeda</InputLabel>
                    <Field as={Select} label="Moeda" name="currency">
                      <MenuItem value="BRL">BRL</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl
                variant="outlined"
                margin="dense"
                className={classes.formControl}
              >
                <InputLabel>Status comercial</InputLabel>
                <Field
                  as={Select}
                  label="Status comercial"
                  name="subscriptionStatus"
                >
                  <MenuItem value="trial">Trial</MenuItem>
                  <MenuItem value="active">Ativa</MenuItem>
                  <MenuItem value="past_due">Em atraso</MenuItem>
                  <MenuItem value="suspended">Suspensa</MenuItem>
                  <MenuItem value="canceled">Cancelada</MenuItem>
                </Field>
              </FormControl>

              <Field
                as={TextField}
                label="Fim do trial"
                name="trialEndsAt"
                type="date"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={touched.trialEndsAt && Boolean(errors.trialEndsAt)}
                helperText={touched.trialEndsAt && errors.trialEndsAt}
              />
              <Field
                as={TextField}
                label="Inicio da cobranca"
                name="billingStartsAt"
                type="date"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={touched.billingStartsAt && Boolean(errors.billingStartsAt)}
                helperText={touched.billingStartsAt && errors.billingStartsAt}
              />
              <Field
                as={TextField}
                label="Vencimento da assinatura"
                name="subscriptionEndsAt"
                type="date"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={
                  touched.subscriptionEndsAt &&
                  Boolean(errors.subscriptionEndsAt)
                }
                helperText={touched.subscriptionEndsAt && errors.subscriptionEndsAt}
              />
              <Field
                as={TextField}
                label="Ultimo pagamento"
                name="lastPaymentAt"
                type="date"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={touched.lastPaymentAt && Boolean(errors.lastPaymentAt)}
                helperText={touched.lastPaymentAt && errors.lastPaymentAt}
              />

              <FormControl
                variant="outlined"
                margin="dense"
                className={classes.formControl}
              >
                <InputLabel>SIP da empresa</InputLabel>
                <Field as={Select} label="SIP da empresa" name="sipEnabled">
                  <MenuItem value={false}>Desabilitado</MenuItem>
                  <MenuItem value={true}>Habilitado</MenuItem>
                </Field>
              </FormControl>

              <Field
                as={TextField}
                label="Dominio SIP"
                name="sipDomain"
                variant="outlined"
                margin="dense"
                fullWidth
                placeholder="ex: empresa.seudominio.com"
                error={touched.sipDomain && Boolean(errors.sipDomain)}
                helperText={touched.sipDomain && errors.sipDomain}
              />

              <FormControl
                variant="outlined"
                margin="dense"
                className={classes.formControl}
                disabled={!sipEnabledSelected}
              >
                <InputLabel>Transporte SIP</InputLabel>
                <Field as={Select} label="Transporte SIP" name="sipTransport">
                  <MenuItem value="udp">UDP</MenuItem>
                  <MenuItem value="tcp">TCP</MenuItem>
                  <MenuItem value="tls">TLS</MenuItem>
                </Field>
              </FormControl>

              <Field
                as={TextField}
                label="SIP media address (opcional)"
                name="sipMediaAddress"
                variant="outlined"
                margin="dense"
                fullWidth
                                      placeholder="ex: 203.0.113.10"
                error={touched.sipMediaAddress && Boolean(errors.sipMediaAddress)}
                helperText={touched.sipMediaAddress && errors.sipMediaAddress}
                disabled={!sipEnabledSelected}
              />

              {isEditMode ? (
                <>
                  <Field
                    as={TextField}
                    label="Prazo de carencia"
                    name="graceEndsAt"
                    type="date"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={touched.graceEndsAt && Boolean(errors.graceEndsAt)}
                    helperText={touched.graceEndsAt && errors.graceEndsAt}
                  />
                  <Field
                    as={TextField}
                    label="Suspensao programada"
                    name="scheduledSuspensionAt"
                    type="date"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={
                      touched.scheduledSuspensionAt &&
                      Boolean(errors.scheduledSuspensionAt)
                    }
                    helperText={
                      touched.scheduledSuspensionAt &&
                      errors.scheduledSuspensionAt
                    }
                  />
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.formControl}
                  >
                    <InputLabel>Status</InputLabel>
                    <Field as={Select} label="Status" name="status">
                      <MenuItem value="active">Ativa</MenuItem>
                      <MenuItem value="inactive">Inativa</MenuItem>
                    </Field>
                  </FormControl>

                  <Field
                    as={TextField}
                    label="Nome do admin principal"
                    name="primaryAdminName"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    error={
                      touched.primaryAdminName && Boolean(errors.primaryAdminName)
                    }
                    helperText={touched.primaryAdminName && errors.primaryAdminName}
                  />
                  <Field
                    as={TextField}
                    label="Email do admin principal"
                    name="primaryAdminEmail"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    error={
                      touched.primaryAdminEmail && Boolean(errors.primaryAdminEmail)
                    }
                    helperText={
                      touched.primaryAdminEmail && errors.primaryAdminEmail
                    }
                  />
                  <Field
                    as={TextField}
                    label="Nova senha do admin principal"
                    name="primaryAdminPassword"
                    type="password"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    error={
                      touched.primaryAdminPassword &&
                      Boolean(errors.primaryAdminPassword)
                    }
                    helperText={
                      touched.primaryAdminPassword && errors.primaryAdminPassword
                    }
                  />
                  <Field
                    as={TextField}
                    label="Data de suspensao"
                    name="suspendedAt"
                    type="date"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={touched.suspendedAt && Boolean(errors.suspendedAt)}
                    helperText={touched.suspendedAt && errors.suspendedAt}
                  />
                  <Field
                    as={TextField}
                    label="Motivo operacional/comercial"
                    name="statusReason"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    multiline
                    minRows={2}
                    error={touched.statusReason && Boolean(errors.statusReason)}
                    helperText={touched.statusReason && errors.statusReason}
                  />
                  <Field
                    as={TextField}
                    label="Observacoes internas"
                    name="internalNotes"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    multiline
                    minRows={3}
                    error={touched.internalNotes && Boolean(errors.internalNotes)}
                    helperText={touched.internalNotes && errors.internalNotes}
                  />
                </>
              ) : (
                <>
                  <Field
                    as={TextField}
                    label="Nome do admin"
                    name="adminName"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    error={touched.adminName && Boolean(errors.adminName)}
                    helperText={touched.adminName && errors.adminName}
                  />
                  <Field
                    as={TextField}
                    label="Email do admin"
                    name="adminEmail"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    error={touched.adminEmail && Boolean(errors.adminEmail)}
                    helperText={touched.adminEmail && errors.adminEmail}
                  />
                  <Field
                    as={TextField}
                    label="Senha inicial do admin"
                    name="adminPassword"
                    type="password"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    error={
                      touched.adminPassword && Boolean(errors.adminPassword)
                    }
                    helperText={touched.adminPassword && errors.adminPassword}
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={onClose}
                color="secondary"
                disabled={isSubmitting}
                variant="outlined"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isSubmitting}
                variant="contained"
                className={classes.btnWrapper}
              >
                {isEditMode ? "Salvar" : "Criar empresa"}
                {isSubmitting && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}}
      </Formik>
    </Dialog>
  );
};

export default CompanyModal;
